// ────────────────────────────────────────────────────────────────────────────
//  usePunch.ts — Stempeluhr (Live-Status, Records, Stats)
//
//  Worker-Endpunkte:
//    GET  /punch/status              — { isClockedIn, isPaused, currentRecord, currentPause }
//    POST /punch/in {note?}          — Einstempeln
//    POST /punch/out {note?}         — Ausstempeln
//    POST /punch/pause               — Pause start
//    POST /punch/resume              — Pause end
//    GET  /punch/records?from=&to=   — Records-Liste (paginated)
//    PUT  /punch/records/:id {note}  — Notiz ändern
//    DELETE /punch/records/:id       — löschen
//    GET  /punch/stats?period=week   — { totalWorked, totalPause, days }
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react'
import { api } from './api'

export interface PunchRecord {
  id:           number
  userId:       string
  orgId:        number | null
  clockIn:      string
  clockOut:     string | null
  pauseSeconds: number
  note:         string | null
  isManual:     boolean
}

export interface PunchPause {
  id:            number
  punchRecordId: number
  pausedAt:      string
  resumedAt:     string | null
}

export interface PunchStatus {
  isClockedIn:   boolean
  isPaused:      boolean
  currentRecord: PunchRecord | null
  currentPause:  PunchPause | null
}

// ── Live-Status (mit Polling) ───────────────────────────────────────────────
export function usePunchStatus(opts?: { pollMs?: number }) {
  const [status, setStatus] = useState<PunchStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const r = await api.get<PunchStatus>('/punch/status')
      setStatus(r)
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    void refresh()
    const ms = opts?.pollMs
    if (!ms) return
    const id = setInterval(refresh, ms)
    return () => clearInterval(id)
  }, [refresh, opts?.pollMs])

  return { status, loading, error, refresh }
}

// ── Records-Liste ──────────────────────────────────────────────────────────
export function usePunchRecords(range: 'today' | 'week' | 'month' | 'all' = 'week') {
  const [items, setItems] = useState<PunchRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      const today = new Date(); today.setHours(0,0,0,0)
      const isoDay = (d: Date) => d.toISOString().slice(0, 10)
      if (range === 'today') {
        params.set('from', isoDay(today))
      } else if (range === 'week') {
        const dow = (today.getDay() + 6) % 7  // Mon = 0
        const start = new Date(today); start.setDate(start.getDate() - dow)
        params.set('from', isoDay(start))
      } else if (range === 'month') {
        const start = new Date(today.getFullYear(), today.getMonth(), 1)
        params.set('from', isoDay(start))
      }
      params.set('pageSize', '200')

      const r = await api.get<{ items: PunchRecord[] }>(`/punch/records?${params}`)
      setItems(r.items ?? [])
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [range])

  useEffect(() => { void refresh() }, [refresh])

  return { items, loading, error, refresh }
}

// ── Actions ────────────────────────────────────────────────────────────────
export async function clockIn(note?: string): Promise<PunchRecord> {
  return api.post<PunchRecord>('/punch/in', note ? { note } : {})
}
export async function clockOut(note?: string): Promise<PunchRecord> {
  return api.post<PunchRecord>('/punch/out', note ? { note } : {})
}
export async function pauseStart(): Promise<PunchPause> {
  return api.post<PunchPause>('/punch/pause', {})
}
export async function pauseEnd(): Promise<PunchPause> {
  return api.post<PunchPause>('/punch/resume', {})
}
export async function updateRecordNote(id: number, note: string): Promise<PunchRecord> {
  return api.put<PunchRecord>(`/punch/records/${id}`, { note })
}
/// Volles Update (Manager / Solo-User): clockIn, clockOut, pauseSeconds, note
export async function updateRecord(id: number, opts: {
  clockIn?:      string
  clockOut?:     string | null
  pauseSeconds?: number
  note?:         string | null
}): Promise<PunchRecord> {
  return api.put<PunchRecord>(`/punch/records/${id}`, opts)
}

/// Records eines anderen Org-Mitglieds laden — Manager/Salary-Permission
/// nötig, sonst 403. Optional from/to als YYYY-MM-DD eingrenzen.
export async function getMemberRecords(userId: string, opts?: { from?: string; to?: string }): Promise<PunchRecord[]> {
  const qs = new URLSearchParams()
  if (opts?.from) qs.set('from', opts.from)
  if (opts?.to)   qs.set('to',   opts.to)
  const q = qs.toString()
  return api.get<PunchRecord[]>(`/punch/team/${encodeURIComponent(userId)}${q ? '?' + q : ''}`)
}
export async function deleteRecord(id: number): Promise<void> {
  await api.delete(`/punch/records/${id}`)
}

// ── Audit-Log ──────────────────────────────────────────────────────────────
export interface AuditEntry {
  id:           number
  recordId:     number
  editorUserId: string
  editorName:   string | null
  targetUserId: string
  targetName:   string | null
  orgId:        number | null
  field:        'clock_in' | 'clock_out' | 'pause_seconds' | 'note' | 'is_manual'
  oldValue:     string | null
  newValue:     string | null
  changedAt:    string
}

export async function getRecordAudit(recordId: number): Promise<AuditEntry[]> {
  const r = await api.get<{ items: AuditEntry[] }>(`/punch/records/${recordId}/audit`)
  return r.items ?? []
}

export async function getTeamAudit(limit = 100): Promise<AuditEntry[]> {
  const r = await api.get<{ items: AuditEntry[] }>(`/punch/team/audit?limit=${limit}`)
  return r.items ?? []
}

/// Liefert true wenn der User Stempelzeit-Records bearbeiten darf:
///   - solo (kein orgId): immer
///   - in org: nur Owner/Admin oder canManageEmployeeProfiles
export function canEditRecords(user: { orgId?: number | null; orgRole?: string | null } | null,
                                perms?: { canManageEmployeeProfiles?: boolean } | null): boolean {
  if (!user) return false
  if (!user.orgId) return true                               // solo
  if (user.orgRole === 'Owner' || user.orgRole === 'Admin') return true
  return perms?.canManageEmployeeProfiles === true
}

// ── Helpers ─────────────────────────────────────────────────────────────────
/// Effektive Sekunden eines Records (Clockout - Clockin - Pause).
export function recordSeconds(r: PunchRecord): number {
  if (!r.clockOut) return 0
  const ms = new Date(r.clockOut).getTime() - new Date(r.clockIn).getTime()
  return Math.max(0, Math.floor(ms / 1000) - (r.pauseSeconds ?? 0))
}

/// Aktuell laufender Record-Zähler in Sekunden (live, von now()).
export function liveSeconds(r: PunchRecord, paused: boolean): number {
  const start = new Date(r.clockIn).getTime()
  const ms = Date.now() - start
  // pauseSeconds + falls aktuell pausiert: Zeit seit pause start
  let totalPause = r.pauseSeconds ?? 0
  if (paused) {
    // wir wissen nicht genau wann die aktuelle Pause anfing; UI passt das via PunchStatus.currentPause.pausedAt an
  }
  return Math.max(0, Math.floor(ms / 1000) - totalPause)
}

/// Formatiert Sekunden zu „Hh Mmin"
export function fmtDuration(seconds: number): { h: number; m: number } {
  const total = Math.max(0, Math.floor(seconds))
  return { h: Math.floor(total / 3600), m: Math.floor((total % 3600) / 60) }
}

// ── Archive ────────────────────────────────────────────────────────────────
export interface PunchArchive {
  id:              number
  userId:          string
  userDisplayName: string | null
  userEmail:       string | null
  orgId:           number | null
  orgName:         string | null
  reason:          'user_reset' | 'org_leave' | 'org_remove' | 'org_delete'
  totalEntries:    number
  totalSeconds:    number
  totalPauseSec:   number
  rangeFrom:       string | null
  rangeTo:         string | null
  createdAt:       string
}

export interface PunchArchiveDetail extends PunchArchive {
  entries: Array<{
    id:           number
    clockIn:      string
    clockOut:     string | null
    pauseSeconds: number
    note:         string | null
    isManual:     boolean
  }>
}

/// User-initiierter Reset — funktioniert nur wenn nicht in einer Org.
export async function resetMyPunch(): Promise<{ ok: true; archiveId: number | null; archivedCount: number }> {
  return api.post<{ ok: true; archiveId: number | null; archivedCount: number }>('/punch/reset', {})
}

export function useMyArchives() {
  const [items, setItems] = useState<PunchArchive[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get<{ items: PunchArchive[] }>('/punch/archives')
      setItems(r.items ?? [])
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { void refresh() }, [refresh])
  return { items, loading, error, refresh }
}

export async function getMyArchive(id: number): Promise<PunchArchiveDetail> {
  return api.get<PunchArchiveDetail>(`/punch/archives/${id}`)
}

export function useTeamArchives() {
  const [items, setItems] = useState<PunchArchive[]>([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const refresh = useCallback(async () => {
    setLoading(true); setForbidden(false); setError(null)
    try {
      const r = await api.get<{ items: PunchArchive[] }>('/punch/team/archives')
      setItems(r.items ?? [])
    } catch (e: any) {
      if (e?.status === 403) setForbidden(true)
      else setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { void refresh() }, [refresh])
  return { items, loading, forbidden, error, refresh }
}

export async function getTeamArchive(id: number): Promise<PunchArchiveDetail> {
  return api.get<PunchArchiveDetail>(`/punch/team/archives/${id}`)
}
